import {ChangeDetectionStrategy, Component, computed, input, Signal, signal} from '@angular/core';
import {MatIconModule} from "@angular/material/icon";
import {NgClass, PercentPipe} from "@angular/common";
import {MatButtonModule} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {Aksjeeiertype, AksjeklasseModell, OpprettAksjeeiereOgBeholdningerSpesifikasjon, OpprettAksjeeierOgBeholdningSpesifikasjon, SkatteetatenAksjeeierbokModell, SkatteetatenAksjeeierModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {MatMenuModule} from "@angular/material/menu";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {LukkAbonnementer} from "felles-ng2/infrastruktur/rxjs/LukkAbonnementer";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AntallAksjerPerAksjeklasseKomponent} from "web-ng2/moduler/aksjeeierbok/komponenter/antall-aksjer-per-aksjeklasse/antall-aksjer-per-aksjeklasse";
import {erIkkeNil, erNil, standard} from "felles-ng2/støttefunksjoner/nilsjekk";
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {Temporal} from "temporal-polyfill";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {parseInt} from "lodash";
import {antallAksjerPerAksjeklasse} from "web-ng2/moduler/aksjeeierbok/komponenter/aksjeeier-liste/BeholdningModellBase";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {skrivPlainDateSerialisert, skrivPlainDateSerialisertOpt} from "felles-ng2/støttefunksjoner/temporal";
import {RammeInnholdKomponent, RammeMedTittelKomponent} from "web-ng2/delt/komponenter/ramme-med-tittel/ramme-med-tittel";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {LasterDataKomponent} from "web-ng2/delt/komponenter/laster-data/laster-data";
import {MatDialogModule} from "@angular/material/dialog";
import {MatTooltipModule} from "@angular/material/tooltip";
import PlainDate = Temporal.PlainDate;


const debug = initDebugger('OpprettAksjeeiereFraSkatteetatenListeKomponent');

export type SkjemaType = ReturnType<typeof OpprettAksjeeiereFraSkatteetatenListeKomponent.opprettSkjema>;
export type SkjemaRadType = ReturnType<typeof OpprettAksjeeiereFraSkatteetatenListeKomponent.opprettSkjemaRad>;
export type SkjemaRadVerdier = SkjemaRadType['value'];
type Status = 'laster' | 'ikkeFunnet' | 'hentet' | 'feilet';

@Component({
    selector: 'opprett-aksjeeiere-fra-skatteetaten-liste',
    templateUrl: './opprett-aksjeeiere-fra-skatteetaten-liste.html',
    styleUrls: ['./opprett-aksjeeiere-fra-skatteetaten-liste.scss'],
    imports: [
        NgClass,
        MatIconModule,
        MatDialogModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltipModule,
        TranslateModule,
        FormaterPlainDatePipe,
        InlineMeldingKomponent,
        AntallAksjerPerAksjeklasseKomponent,
        LasterDataKomponent,
        MatFormField,
        MatInput,
        MatHint,
        ReactiveFormsModule,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatLabel,
        MatSuffix,
        CdkTextareaAutosize,
        PercentPipe,
        FormsModule,
        RammeMedTittelKomponent,
        MatRadioButton,
        MatRadioGroup,
        RammeInnholdKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpprettAksjeeiereFraSkatteetatenListeKomponent {

    constructor() {
    }

    modell = input.required<OpprettAksjeeiereFraSkatteetatenModell>();

    static opprettSkjemaRad() {
        return new FormGroup({
            id: new FormControl(nyUuid(), {nonNullable: true}), //ikke eksponert for redigering - AksjeeierId
            type: new FormControl(Aksjeeiertype.person, {nonNullable: true}),
            navn: new FormControl('', {validators: FeltValidatorer.litenIkkeTomStreng}),
            adresse: new FormControl('', {validators: FeltValidatorer.storStreng}),
            epost: new FormControl('', {validators: FeltValidatorer.epost}),
            foedselsdato: new FormControl<PlainDate | null>(null),
            innfoeringsdato: new FormControl<PlainDate | null>(null, {validators: Validators.required}),
            organisasjonsnummer: new FormControl('', FeltValidatorer.litenStreng),
            // setter denne til string
            antallAksjer: new FormControl<string>('', [Validators.required, Validators.pattern(/^\d+$/)]),
            aksjeklasse: new FormControl<string>('')
        });
    }

    static opprettSkjema(): FormGroup<{ fellesInnfoeringsdato: FormControl<PlainDate | null>, aksjeeiere: FormArray<SkjemaRadType> }> {
        return new FormGroup({
            fellesInnfoeringsdato: new FormControl<PlainDate | null>(null),
            aksjeeiere: new FormArray<SkjemaRadType>([])
        })

    }


    static opprettSkjemaRadOgPatch(modell: SkatteetatenAksjeeierModell | null) {
        let skjema = OpprettAksjeeiereFraSkatteetatenListeKomponent.opprettSkjemaRad();
        if (erIkkeNil(modell))
            skjema.patchValue(OpprettAksjeeiereFraSkatteetatenListeKomponent.somSkjemaVerdier(modell))
        return skjema;
    }

    static somSkjemaVerdier(modell: SkatteetatenAksjeeierModell): SkjemaRadVerdier {
        return {
            type: erNil(modell.orgnummer) ? Aksjeeiertype.person : Aksjeeiertype.organisasjon,
            navn: standard(modell.navn, ''),
            adresse: OpprettAksjeeiereFraSkatteetatenListeKomponent.tilAdresse(modell),
            organisasjonsnummer: modell.orgnummer,
            antallAksjer: modell.antallAksjer.toString(),
            aksjeklasse: modell.aksjeklasse
        };
    }

    static tilAdresse(modell: SkatteetatenAksjeeierModell) {
        let adr = '';
        if (erIkkeNil(modell.postNummerOgSted)) {
            adr = modell.postNummerOgSted;
        }

        if (erIkkeNil(modell.landkode)) {
            if (adr.length > 0) {
                adr = adr + ', ';

            }
            adr = adr + modell.landkode;
        }
        return adr;
    }

    leggTilNyRad(aksjeeierType: Aksjeeiertype) {
        this.modell()!.leggTilNyRad(aksjeeierType);
    }


    protected readonly erIkkeNil = erIkkeNil;
    aksjeeiertypePerson = Aksjeeiertype.person
    aksjeeiertypeOrganisasjon = Aksjeeiertype.organisasjon

}

export class OpprettAksjeeiereFraSkatteetatenModell {
    skatteetatenModell: Signal<SkatteetatenAksjeeierbokModell | null> = signal(null);
    skatteetatenDato = signal(PlainDate.from('2023-12-31'));
    modeller = signal<BeholdingOgAksjeeierSkjemaModell[]>([]);
    totaltAntallAksjer = computed(() =>
      this.modeller().filter(b => !b.slettet()).map(m => m.antallAksjer()).reduce((a, b) => a + b, 0));

    totaltAntallAksjerPerAksjeklasse = computed(() =>
      antallAksjerPerAksjeklasse(this.modeller().filter(b => !b.slettet()).map(beholdning => {
          return {aksjeklasse: beholdning.aksjeklasse(), antallAksjer: beholdning.antallAksjer()};
      }), this.oversettelser));

    redigererRad = computed(() => this.modeller().filter(m => m.redigerer()).length > 0)

    constructor(public status: Status, public skjema: SkjemaType, modell: SkatteetatenAksjeeierbokModell | null, private ab: LukkAbonnementer, private oversettelser: Oversettelser, skatteetatenDato: PlainDate) {
        this.skatteetatenDato.set(skatteetatenDato);
        if (status == 'hentet') {
            if (erNil(modell)) {
                throw new Error("Kan ikke ha at modell er null når status er hentet");
            }
            this.skatteetatenModell = signal<SkatteetatenAksjeeierbokModell>(modell);
            this.modeller.set(modell.aksjeeiere.map(e => new BeholdingOgAksjeeierSkjemaModell(this, e, this.totaltAntallAksjer, ab)));
        }

        this.modeller().forEach(modell => this.skjema.controls.aksjeeiere.push(modell.skjema))

        ab.reg(this.skjema.controls.fellesInnfoeringsdato.statusChanges
          .subscribe(() => {
              if (this.skjema.controls.fellesInnfoeringsdato.invalid) {
                  return
              }
              this.fellesDatoOppdatert(this.skjema.controls.fellesInnfoeringsdato.value)
          }));
    }


    lagSpekk(): OpprettAksjeeiereOgBeholdningerSpesifikasjon {
        return {
            beholdninger: this.lagBeholdningerSpekk()
        }
    }

    lagBeholdningerSpekk(): OpprettAksjeeierOgBeholdningSpesifikasjon[] {
        return this.modeller().map(m => m.lagSpesifikasjon())
    }

    fellesDatoOppdatert(nydato: PlainDate | null) {
        if (erNil(nydato)) return; // fjerner ikke de som er satt
        this.skjema.controls.aksjeeiere.controls.forEach(m => m.controls.innfoeringsdato.patchValue(nydato));
    }

    klikkSlett(beholdning: BeholdingOgAksjeeierSkjemaModell) {
        beholdning.slettet.set(true);
        if (beholdning.redigerer()) {
            beholdning.redigerer.set(false);
        }
        if (!beholdning.erFraSkatteetaten()) {
            this.modeller.set(this.modeller().filter(m => m.skjema.controls.id.value !== beholdning.skjema.controls.id.value));
        }
        const index = this.skjema.controls.aksjeeiere.controls.findIndex((skjema) => skjema.controls.id.value === beholdning.skjema.controls.id.value);
        if (index >= 0)
            this.skjema.controls.aksjeeiere.removeAt(index);
    }

    klikkAngreSlett(beholdning: BeholdingOgAksjeeierSkjemaModell) {
        if (this.redigererRad()) return; // ikke gjør noe hvis vi redigerer en annen rad. lenken er vis om ikke klikkbar.
        if (!beholdning.slettet()) return;
        beholdning.klikkAngreSlett();
        this.skjema.controls.aksjeeiere.push(beholdning.skjema);
    }

    leggTilNyRad(aksjeeierType: Aksjeeiertype) {
        const modell = new BeholdingOgAksjeeierSkjemaModell(this, null, this.totaltAntallAksjer, this.ab);
        modell.skjema.controls.type.patchValue(aksjeeierType);
        this.modeller.set([modell, ...this.modeller()]);
        this.skjema.controls.aksjeeiere.insert(0, modell.skjema);
        modell.klikkRediger();
    }

    alleInnfoeringsDatoLagtInn(): boolean {
        return this.modeller()!.filter(m => m.innfoeringsdatoUgyldig()).length === 0;
    }

}


function zeroIfNull(tall: any | null): number {
    if (erNil(tall)) return 0;
    return parseInt(tall);
}

export class BeholdingOgAksjeeierSkjemaModell {
    modell: SkatteetatenAksjeeierModell | null; // null når man legger inn ny rad
    skjema: SkjemaRadType;
    totaltAntallAksjer: Signal<number>;
    antallAksjer = signal(0);
    andel = computed(() => this.antallAksjer() / this.totaltAntallAksjer())
    aksjeklasse = signal<AksjeklasseModell | null>(null);
    slettet = signal(false);
    redigerer = signal(false);
    erFraSkatteetaten = signal(true);
    erNyOgIkkeLagret = signal(false); // ny og ikke lagret ennå.
    visPersonOrganisasjonsValg = signal(false);


    constructor(private forelder: OpprettAksjeeiereFraSkatteetatenModell, modell: SkatteetatenAksjeeierModell | null, totaltAntallAksjer: Signal<number>, ab: LukkAbonnementer) {
        this.totaltAntallAksjer = totaltAntallAksjer;
        this.modell = modell;
        this.skjema = OpprettAksjeeiereFraSkatteetatenListeKomponent.opprettSkjemaRadOgPatch(modell);
        if (erNil(this.modell)) {
            this.erFraSkatteetaten.set(false);
            this.erNyOgIkkeLagret.set(true);
            this.visPersonOrganisasjonsValg.set(true);
        } else {
            if (erNil(this.modell.foedselsAar) && erNil(this.modell.orgnummer)) {
                this.visPersonOrganisasjonsValg.set(true);
            }
        }

        this.antallAksjer.set(this.antallAksjerNullsikker());
        this.aksjeklasse.set(this.somAksjeklasseModell());
        ab.reg(this.skjema.controls.antallAksjer.statusChanges
          .subscribe(() => {
              this.antallAksjer.set(this.antallAksjerNullsikker());
          }));

        ab.reg(this.skjema.controls.aksjeklasse.statusChanges
          .subscribe(() => {
              this.aksjeklasse.set(this.somAksjeklasseModell());
          }));
    }

    somAksjeklasseModell(): AksjeklasseModell | null {
        if (erNil(trimTilNull(this.skjema.controls.aksjeklasse.value))) return null;
        return {id: this.skjema.controls.aksjeklasse.value!, navn: this.skjema.controls.aksjeklasse.value!, beskrivelse: ''}
    }

    antallAksjerNullsikker() {
        if (this.skjema.controls.antallAksjer.valid)
            return zeroIfNull(this.skjema.controls.antallAksjer.value);
        return zeroIfNull(0);
    }

    startFoedselsdato() {
        if (this.erOrganisasjon() || erNil(this.modell))
            return null;
        if (erNil(this.modell.foedselsAar))
            return null;
        return new PlainDate(parseInt(this.modell.foedselsAar!), 1, 1);
    }

    erOrganisasjon(): boolean {
        return this.skjema.controls.type.value === Aksjeeiertype.organisasjon;
    }

    erPerson(): boolean {
        return !this.erOrganisasjon();
    }

    kanSkjemaLukkes(): boolean {
        if (this.skjema.valid) return true;
        // kun feil på innføringsdato er ok

        let kanLukkes = true;
        Object.keys(this.skjema.controls).forEach(key => {
            // this.skjema.controls.innfoeringsdato
            if (key !== 'innfoeringsdato') {
                if (this.skjema.get(key)!.invalid)
                    kanLukkes = false;
            }

        });
        return kanLukkes;
    }

    innfoeringsdatoUgyldig(): boolean {
        return this.skjema.controls.innfoeringsdato.invalid;
    }

    klikkRediger() {
        // skal ikke være mulig om de er slettet
        this.redigerer.set(true);
    }

    klikkRedigeringFerdig() {
        this.redigerer.set(false);
        if (!this.erFraSkatteetaten()) {
            this.erNyOgIkkeLagret.set(false);
        }
    }

    klikkAngreSlett() {
        this.slettet.set(false);
        if (!this.kanSkjemaLukkes())
            this.klikkRediger();
    }

    resett() {
        if (erIkkeNil(this.modell)) {
            this.skjema.reset(OpprettAksjeeiereFraSkatteetatenListeKomponent.somSkjemaVerdier(this.modell));
        } else {
            this.skjema.reset();
            this.forelder.klikkSlett(this);
        }
        this.redigerer.set(false);
    }

    lagSpesifikasjon(): OpprettAksjeeierOgBeholdningSpesifikasjon {
        return {
            id: this.skjema.controls.id.value,
            type: this.skjema.controls.type.value,
            navn: this.skjema.controls.navn.value!,
            organisasjonsnummer: this.erOrganisasjon() ? trimTilNull(this.skjema.controls.organisasjonsnummer.value) : null,
            foedselsdato: this.erPerson() ? skrivPlainDateSerialisertOpt(this.skjema.controls.foedselsdato.value) : null,
            antallAksjer: zeroIfNull(this.skjema.controls.antallAksjer.value),
            adresse: trimTilNull(this.skjema.controls.adresse.value),
            aksjeklasse: trimTilNull(this.skjema.controls.aksjeklasse.value),
            epost: trimTilNull(this.skjema.controls.epost.value),
            innfoeringsdato: skrivPlainDateSerialisert(this.skjema.controls.innfoeringsdato.value!)
        };
    }
}



